/* The Grain mark: a marigold ring struck with a single tilted grain.

   One glyph, one definition. Before this the mark existed as a CSS
   `border-radius` + `border` circle hand-rolled in g-header, g-signin and
   g-onboarding — three copies that had already drifted from the packaged icons,
   which carry a centre grain the CSS ones never had. ADR 0003's lesson (three
   copies of one treatment, differing only in size) applied to the logo itself.

   The geometry is the 1024 grid in scripts/gen_icons.py, which draws every PNG,
   both SVGs and the .ico from the same six numbers. This file is the one place
   they are restated — a TS module cannot be generated at request time — so the
   two change together, in one commit. ADR 0022.

   Not folded into g-icon: that registry is 24×24 stroke-based UI glyphs on a
   shared 1.6 stroke, and a brand mark in it is an invitation to use the logo as
   an affordance. Constitution §29 — the wordmark performs no action. */

import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';

@customElement('g-mark')
export class GMark extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: inline-flex;
      width: var(--mark-size, 18px);
      height: var(--mark-size, 18px);
      /* Sized like g-icon's --icon-size and g-avatar's --avatar-size: a custom
         property inherits through the shadow boundary, so one declaration on
         the host element is enough (ADR 0009 decision 2).

         The accent rather than currentColor, because the mark sits beside the
         wordmark and must not take its ink. This is what makes it #e8a13d on
         a dark ground and #c8811f on a light one, matching whichever packaged
         icon is showing. */
      color: var(--accent);
      flex: none;
    }
    svg {
      width: 100%;
      height: 100%;
    }
  `];

  override render() {
    /* The tight crop of the 1024 grid — the glyph spans 168..856 — so the mark
       sits flush against the wordmark with no built-in padding. The packaged
       tiles render the full `0 0 1024 1024` box instead, which is where the
       field's breathing room comes from. Same geometry, different crop. */
    return html`<svg viewBox="168 168 688 688" aria-hidden="true">
      <circle cx="512" cy="512" r="296" fill="none"
              stroke="currentColor" stroke-width="96"></circle>
      <ellipse cx="512" cy="512" rx="72" ry="116" fill="currentColor"
               transform="rotate(-14 512 512)"></ellipse>
    </svg>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-mark': GMark;
  }
}
